import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { map, of, startWith, switchMap } from 'rxjs';
import { TableContentItemFormGroup } from '../question-content-item.model';

@Component({
  selector: 'dojo-question-table-form-viewer',
  template: `
    <div class="flex w-full max-w-[900px] flex-col">
      @if (state.fillInTheBlanksValue) {
        <div class="mb-2 flex w-full items-center justify-center gap-2">
          <span class="badge-above bg-neutral-50 text-neutral-600">Fill in the blanks</span>
        </div>
      }
      <div class="flex w-full justify-center">
        <div class="w-full" [style]="state.formValue.style">
          <dojo-markdown-viewer class="w-full" [content]="state.formValue.content ?? ''" />
          @if (state.formValue.title) {
            <div class="mb-2 mt-2 w-full text-center">
              <span class="table-title text-sm font-medium text-neutral-700">{{ state.formValue.title }}</span>
            </div>
          }
        </div>
      </div>
      @if (!state.formValue.content) {
        <div class="mt-3 flex w-full justify-center">
          <span class="text-gray-500">No table</span>
        </div>
      }
    </div>
  `,
  imports: [MarkdownViewerComponent],
  host: {
    class: 'flex w-full gap-2 min-h-10',
  },
  styles: [
    `
      .badge-above {
        @apply rounded px-2 py-1 text-xs font-medium tracking-wide;
      }

      .table-title {
        @apply leading-relaxed;
        max-width: 100%;
        word-wrap: break-word;
      }

      .table-description {
        @apply leading-relaxed;
        max-width: 100%;
        word-wrap: break-word;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionTableFormViewerComponent {
  readonly formCtrl = input.required<TableContentItemFormGroup>();

  readonly formValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) => formCtrl.valueChanges.pipe(startWith(formCtrl.value))),
  );

  readonly fillInTheBlanksValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) =>
      formCtrl.controls.fill_in_the_blanks
        ? formCtrl.controls.fill_in_the_blanks.valueChanges.pipe(startWith(formCtrl.controls.fill_in_the_blanks.value))
        : of(false),
    ),
    map((value) => Boolean(value)),
  );

  readonly state = connectState({
    formValue: this.formValue$,
    fillInTheBlanksValue: this.fillInTheBlanksValue$,
  });
}
